import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { X } from "lucide-react";

import { cn } from "utils";
import {
  ExperienceLevels,
  EXPERIENCE_LABELS,
  normalizeExperienceLevel,
} from "components/UserCardList/components/UserCardResult/constants";
import { useSkillDisplayName } from "./hooks";
import { icons } from "./skillsOnCloudinary";

const sizeStyles = {
  sm: {
    container: "min-h-[24px] px-2 py-1 text-xs",
    icon: "h-4 min-w-4 px-1 text-[10px]",
    image: "h-4 w-4",
    cloudinarySize: 32,
    gap: "gap-1.5",
  },
  md: {
    container: "min-h-[34px] px-3 py-2 text-sm",
    icon: "h-5 min-w-5 px-1.5 text-[11px]",
    image: "h-5 w-5",
    cloudinarySize: 48,
    gap: "gap-2",
  },
};

const sanitizeSkillName = (name = "") =>
  name.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

const EXPERIENCE_LEVEL_CLASSES = {
  [ExperienceLevels.HIGH]: {
    container: "bg-skillExperience-high-bg",
    icon: "bg-skillExperience-high-icon text-white",
    highlight: "ring-2 ring-skillExperience-high",
  },
  [ExperienceLevels.MEDIUM]: {
    container: "bg-skillExperience-medium-bg",
    icon: "bg-skillExperience-medium-icon text-white",
    highlight: "ring-2 ring-skillExperience-medium",
  },
  [ExperienceLevels.LOW]: {
    container: "bg-skillExperience-low-bg",
    icon: "bg-skillExperience-low-icon text-white",
    highlight: "ring-2 ring-skillExperience-low",
  },
};

const formatExperienceLabel = (experience) => {
  const level = normalizeExperienceLevel(experience);

  if (level && EXPERIENCE_LABELS[level]) {
    return EXPERIENCE_LABELS[level];
  }

  if (!experience) {
    return "";
  }

  return experience.charAt(0).toUpperCase() + experience.slice(1).toLowerCase();
};

const getSkillGlyph = (displayName, category) => {
  const normalized = displayName.toLowerCase();

  if (normalized.includes("javascript")) return "JS";
  if (normalized.includes("typescript")) return "TS";
  if (normalized.includes("html")) return "5";
  if (normalized.includes("css")) return "CSS";
  if (normalized.includes("python")) return "Py";
  if (normalized.includes("node")) return "Nd";
  if (normalized.includes("react")) return "R";
  if (normalized.includes("angular")) return "A";
  if (normalized.includes("java")) return "J";
  if (category === "CERTIFICATION") return "C";
  if (category === "LANGUAGE") return "L";
  if (category === "SOFT") return "S";
  if (category === "PROFESSIONAL") return "P";

  return displayName.slice(0, 2).toUpperCase();
};

const SkillIcon = ({ displayName, category, currentSize }) => {
  const [imageError, setImageError] = useState(false);
  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUDNAME;
  const sanitizedSkillName = useMemo(
    () => sanitizeSkillName(displayName),
    [displayName]
  );
  const publicId = `icons/skills/mono/${sanitizedSkillName}`;

  useEffect(() => {
    setImageError(false);
  }, [sanitizedSkillName]);

  if (cloudName && icons.has(sanitizedSkillName) && !imageError) {
    const imageUrl = `https://res.cloudinary.com/${cloudName}/image/upload/w_${
      currentSize.cloudinarySize * 2
    },h_${currentSize.cloudinarySize * 2},f_auto,q_auto/${publicId}.svg`;

    return (
      <>
        <img
          src={imageUrl}
          alt=""
          aria-hidden="true"
          className="hidden"
          onError={() => setImageError(true)}
        />
        <span
          aria-hidden="true"
          className={cn(currentSize.image, "inline-block shrink-0")}
          style={{
            backgroundColor: "currentColor",
            WebkitMask: `url(${imageUrl}) no-repeat center / contain`,
            mask: `url(${imageUrl}) no-repeat center / contain`,
          }}
        />
      </>
    );
  }

  return getSkillGlyph(displayName, category);
};

const SkillBadge = ({
  skillName,
  className,
  onRemove,
  removable,
  size,
  category,
  hasSkill,
  noUppercase,
  experience,
  isHighlighted,
}) => {
  const displayName = useSkillDisplayName(skillName);
  const currentSize = sizeStyles[size];
  const isMuted = !hasSkill;
  const experienceLevel = normalizeExperienceLevel(experience);
  const experienceClasses = useMemo(
    () =>
      !isMuted && experienceLevel
        ? EXPERIENCE_LEVEL_CLASSES[experienceLevel]
        : null,
    [experienceLevel, isMuted]
  );

  return (
    <div
      className={cn(
        "inline-flex max-w-full items-center justify-center rounded-md font-inter transition",
        currentSize.container,
        currentSize.gap,
        {
          "bg-slate-100": isMuted || !experienceClasses,
          [experienceClasses?.container]: experienceClasses,
          [experienceClasses?.highlight]: experienceClasses && isHighlighted,
        },
        className
      )}
      title={displayName}
    >
      <span
        className={cn(
          "inline-flex shrink-0 items-center justify-center rounded leading-none",
          currentSize.icon,
          {
            "bg-slate-200 text-slate-500": isMuted || !experienceClasses,
            [experienceClasses?.icon]: experienceClasses,
          }
        )}
      >
        <SkillIcon
          displayName={displayName}
          category={category}
          currentSize={currentSize}
        />
      </span>
      <span
        className={cn(
          "min-w-0 truncate font-inter text-sm font-medium leading-normal text-[#334155]",
          {
            uppercase: !noUppercase,
            "normal-case": noUppercase,
          }
        )}
      >
        {displayName}
      </span>
      {experience && (
        <>
          <span className="h-px w-3 shrink-0 rotate-90 bg-[#334155] opacity-25" />
          <span className="shrink-0 font-inter text-xs font-normal leading-normal text-[#334155]">
            {formatExperienceLabel(experience)}
          </span>
        </>
      )}
      {removable && onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-white transition hover:opacity-80"
          aria-label={`Remove ${displayName}`}
        >
          <X size={10} className="text-gray-500" />
        </button>
      )}
    </div>
  );
};

SkillBadge.propTypes = {
  skillName: PropTypes.string.isRequired,
  className: PropTypes.string,
  onRemove: PropTypes.func,
  removable: PropTypes.bool,
  size: PropTypes.oneOf(["sm", "md"]),
  category: PropTypes.string,
  hasSkill: PropTypes.bool,
  noUppercase: PropTypes.bool,
  experience: PropTypes.string,
  isHighlighted: PropTypes.bool,
};

SkillBadge.defaultProps = {
  className: "",
  onRemove: null,
  removable: false,
  size: "md",
  category: "",
  hasSkill: true,
  noUppercase: false,
  experience: "",
  isHighlighted: false,
};

SkillIcon.propTypes = {
  displayName: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  currentSize: PropTypes.shape({
    image: PropTypes.string.isRequired,
    cloudinarySize: PropTypes.number.isRequired,
  }).isRequired,
};

export default SkillBadge;
